import React, {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  DeprecatedIcon,
  Flex,
  Icon,
  InputGroup,
  Pane,
  Popover,
  Tooltip,
  Typography,
} from '@fullstory/components';
import {
  getIntervals,
  IntervalOption,
  timeIntervalOptions as timeIntervalOptionsMap,
} from './utils';
import { TimeInterval, TimeRange } from '@fullstory/proto/fs/api/segment';
import { TimeRangeComparison } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import {
  DateRangeComparison,
  dateRangeToString,
  getCompareText,
  timeRangeToDateRange,
  toDateRangeComparison,
  toTimeRangeComparison,
} from '../../utils/comparison';
import moment from 'moment';
import { MultiColumnTimePicker } from './MultiColumnTimePicker';
import { DateRangeID } from '@fullstory/client-api';
import { capitalize } from '@fullstory/util/fmt';
import { DateRange, DateRangeOption } from '../../types/dateRange';
import {
  dateRangeById,
  getDateRangeIdFromTimeRange,
  sinceDateRangeEnd,
} from '@fullstory/search-model';
import { getDateRange } from '@fullstory/util/date';
import { isNonNull } from '@fullstory/typeutils';
import { useTimeRangeOptions } from '../../utils/timeRange';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';
import { fullstorySelectors } from '@fullstory/redux';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
import { useFlags } from '@fullstory/react-hooks';

export const testIds = {
  BUTTON_ON_APPLY: 'button-on-apply',
  BUTTON_ON_CANCEL: 'button-on-cancel',
  BUTTON_PRIMARY_RANGE: 'button-primary-range',
  BUTTON_COMPARE: 'button-compare',
  BUTTON_COMPARE_CLEAR: 'button-compare-clear',
};

export type Values = {
  primaryRange: TimeRange | null;
  comparison: TimeRangeComparison | null;
  interval: TimeInterval | null;
};

export type ButtonProps = Pick<
  ComponentProps<typeof Button>,
  'size' | 'variant' | 'iconLeft' | 'fullWidth'
>;
export const buttonSizes = ['x-small', 'small', 'medium', 'large'] as const;
export type ButtonSize = (typeof buttonSizes)[number];
export const buttonVariants = [
  'fill-primary',
  'fill-secondary',
  'fill-builder',
  'outline',
  'minimal',
  'underline-builder',
] as const;
export type ButtonVariant = (typeof buttonVariants)[number];

export interface MultiColumnTimePickerWrapperProps extends Values {
  onApply: (values: Values) => void;
  comparisonEnabled?: boolean;
  primaryRangeOptions: DateRangeOption[];
  includeEntireRange?: boolean;
  /**
   * Optionally specify intervals to allow
   */
  timeIntervalOptions?: TimeInterval[];
  placeholder?: string;
  buttonProps?: ButtonProps;
  maxNumDays: number;
  comparisonButtonDisabled?: boolean;
  comparisonButtonDisabledMsg?: string;
  popoverPlacement?: ComponentProps<typeof Popover>['placement'];
  showRetention?: boolean;
  /**
   * If `true`, a button will show up next to the primary range dropdown that will clear the all values.
   * Defaults to `false`.
   */
  clearable?: boolean;
  onMetricDetailForm?: boolean;
  flexWrap?: ComponentProps<typeof Flex>['flexWrap'];
  flexReverse?: ComponentProps<typeof Flex>['reverse'];
  justifyContent?: ComponentProps<typeof Flex>['justifyContent'];
}

export const MultiColumnTimePickerWrapper: React.FC<MultiColumnTimePickerWrapperProps> = ({
  onApply,
  comparisonEnabled = true,
  primaryRange: injectedPrimaryRange,
  comparison: injectedComparison,
  interval: injectedInterval,
  primaryRangeOptions,
  includeEntireRange = false,
  timeIntervalOptions,
  placeholder,
  buttonProps,
  maxNumDays,
  comparisonButtonDisabled = false,
  comparisonButtonDisabledMsg,
  popoverPlacement = 'bottom-end',
  showRetention = true,
  clearable = false,
  onMetricDetailForm,
  flexWrap = 'nowrap',
  flexReverse = false,
  justifyContent = 'flex-start',
}) => {
  const flags = useFlags();
  const includeMinute = flags.featureFlags().AppTrendsMinutely;
  const anchor = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const openPopover = () => setOpen(true);
  const closePopover = () => setOpen(false);
  const [focusComparison, setFocusComparison] = useState(false);

  const now = useMemo(() => moment().utc().toDate(), []);

  const intervals = timeIntervalOptions
    ? timeIntervalOptions.reduce<IntervalOption[]>((acc, interval) => {
        const option = timeIntervalOptionsMap[interval];
        if (option) acc.push(option);
        return acc;
      }, [])
    : getIntervals({ includeMinute, includeEntireRange });

  const [primaryRange, setPrimaryRange] = useState<DateRange | null>(
    injectedPrimaryRange ? timeRangeToDateRange(injectedPrimaryRange, now) : null,
  );
  const [dateRangeId, setDateRangeId] = useState<DateRangeID | null>(
    injectedPrimaryRange
      ? getDateRangeIdFromTimeRange({
          Start: injectedPrimaryRange.startTime,
          End: injectedPrimaryRange.endTime,
        })
      : null,
  );
  const [comparison, setComparison] = useState<DateRangeComparison | null>(
    toDateRangeComparison(injectedComparison, now),
  );
  const [interval, setInterval] = useState<TimeInterval | null>(injectedInterval);

  const [validIntervalRangeCombo, setValidIntervalRangeCombo] = useState(true);

  const [validCustomRange, setValidCustomRange] = useState(true);

  const endOfToday = useMemo(() => moment(now).add(1, 'day').startOf('day').toDate(), [now]);
  const onDateRangeChange = (id: DateRangeID) => {
    setDateRangeId(id);
    const range = getDateRange(id, now);
    setPrimaryRange({
      start: range.startTime,
      end: id === DateRangeID.Since ? endOfToday : range.endTime,
    });
  };

  useEffect(() => {
    let primaryValid = true;
    let comparisonValid = true;
    if ((dateRangeId === DateRangeID.Custom || dateRangeId === DateRangeID.Since) && primaryRange) {
      primaryValid = isCustomRangeValid(primaryRange, maxNumDays);
    }
    if (comparison?.custom) {
      comparisonValid = isCustomRangeValid(comparison.custom, maxNumDays);
    }
    setValidCustomRange(primaryValid && comparisonValid);
  }, [comparison?.custom, dateRangeId, maxNumDays, primaryRange]);

  const [values, isValidValues] = useMemo(() => {
    try {
      const values = toValues({ primaryRange, dateRangeId, comparison, interval }, now);
      return [values, isNonNull(values.primaryRange)];
    } catch (e) {
      return [null, false];
    }
  }, [primaryRange, dateRangeId, comparison, interval, now]);

  const updatePrimaryRangeAndDateRange = useCallback(() => {
    setPrimaryRange(injectedPrimaryRange ? timeRangeToDateRange(injectedPrimaryRange, now) : null);
    setDateRangeId(
      injectedPrimaryRange
        ? getDateRangeIdFromTimeRange({
            Start: injectedPrimaryRange.startTime,
            End: injectedPrimaryRange.endTime,
          })
        : null,
    );
  }, [injectedPrimaryRange, now]);

  useEffect(() => {
    updatePrimaryRangeAndDateRange();
  }, [updatePrimaryRangeAndDateRange]);

  useEffect(() => {
    setComparison(toDateRangeComparison(injectedComparison, now));
  }, [injectedComparison, now]);

  useEffect(() => {
    setInterval(injectedInterval);
  }, [injectedInterval]);

  const onPrimaryRangeChange = (value: DateRange) => {
    setPrimaryRange(value);
    setDateRangeId(prev => {
      // If anything other than Since is selected, picking a custom date should take you to Custom
      if (prev !== DateRangeID.Since || !isEqual(value.end, endOfToday)) {
        return DateRangeID.Custom;
      }
      return prev;
    });
  };

  // we want the primary range options to soley dictate which options are allowed;
  // any forbidden options (Ex: past year) should already be filtered out of the primary range options
  const options = useTimeRangeOptions({
    timeInterval: interval ?? undefined,
    includeAllTime:
      primaryRangeOptions.filter(option => option.id === DateRangeID.AllTime).length > 0,
  });
  const optionIdsForInterval = useMemo(() => options.map(option => option.id), [options]);

  const primaryRangeOptionsForInterval = useMemo(() => {
    if (!interval) return primaryRangeOptions;

    return primaryRangeOptions?.map(option =>
      optionIdsForInterval.includes(option.id) ? option : { ...option, disabled: true },
    );
  }, [interval, primaryRangeOptions, optionIdsForInterval]);

  useEffect(() => {
    if (dateRangeId) {
      setValidIntervalRangeCombo(optionIdsForInterval.includes(dateRangeId));
    }
  }, [optionIdsForInterval, dateRangeId]);

  const onComparisonClick = () => {
    if (open && comparison) {
      // if popover is already open and there is already a comparison,
      // clicking the comparison button again should apply changes and close the popover
      onApplyClick();
    } else if (!open) {
      // if the popover is closed, clicking the comparison button should always open the popover
      openPopover();
      // focus comparison range
      setFocusComparison(true);
    }
    if (!comparison) {
      // if there is no comparison set, clicking the comparison button should enable comparisons
      setComparison(prev => {
        if (prev) return null;
        return { canned: TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_PERIOD };
      });
    }
  };

  const onComparisonClear = () => {
    setComparison(null);
    if (values) {
      onApply({ ...values, comparison: null });
    }
  };

  const onPrimaryClear = () => {
    setPrimaryRange(null);
    if (values) {
      onApply({ primaryRange: null, comparison: null, interval: null });
    }
  };

  const onPrimaryClick = () => {
    if (open) onApplyClick();
    else {
      openPopover();
      // focus primary range
      setFocusComparison(false);
    }
  };

  const onCancel = useCallback(() => {
    // Reset state on cancel
    updatePrimaryRangeAndDateRange();
    setComparison(toDateRangeComparison(injectedComparison, now));
    setInterval(injectedInterval);

    closePopover();
  }, [updatePrimaryRangeAndDateRange, now, injectedComparison, injectedInterval]);

  const applyDisabled = !isValidValues || !validIntervalRangeCombo || !validCustomRange;

  const onApplyClick = useCallback(() => {
    if (values) {
      onApply(values);
      closePopover();
    }
  }, [onApply, values]);

  const onCloseSignal = useCallback(() => {
    if (applyDisabled) {
      onCancel();
    } else {
      onApplyClick();
    }
  }, [applyDisabled, onApplyClick, onCancel]);

  const retentionTimestamp = useSelector(fullstorySelectors.getRetentionTimestamp);

  return (
    <Flex
      data-component="MultiColumnTimePickerWrapper"
      ref={anchor}
      justifyContent={justifyContent}
      gap={8}
      flexWrap={flexWrap}
      reverse={flexReverse}
    >
      {comparisonEnabled && (
        <Box>
          <CompareButton
            comparison={comparison}
            onClick={onComparisonClick}
            onClear={onComparisonClear}
            buttonProps={buttonProps}
            disabled={comparisonButtonDisabled}
            disabledTooltip={comparisonButtonDisabledMsg}
          />
        </Box>
      )}
      <InputGroup>
        {clearable && injectedPrimaryRange && (
          <Button
            onClick={onPrimaryClear}
            size={buttonProps?.size}
            variant={buttonProps?.variant ?? 'fill-primary'}
            testId="multi-column-time-picker-primary-clear-button"
          >
            <Icon color="activated" name="close" size={16} />
          </Button>
        )}
        <Button
          testId={testIds.BUTTON_PRIMARY_RANGE}
          onClick={onPrimaryClick}
          variant="fill-builder"
          iconRight="chevron-down"
          {...buttonProps}
        >
          {getRangeButtonText(
            { primaryRange, dateRangeId, interval },
            primaryRangeOptions,
            intervals,
            placeholder,
          )}
        </Button>
      </InputGroup>
      <Popover
        anchor={anchor.current}
        open={open}
        onCloseSignal={onCloseSignal}
        placement={popoverPlacement}
        offset={8}
        flipY={false}
        flipX={false}
        positioningStrategy="fixed"
        variant="bordered"
        preventOverflow={16}
      >
        <Pane width="min-content" overflow="hidden">
          <Flex column>
            <MultiColumnTimePicker
              primaryRange={primaryRange}
              onPrimaryRangeChange={onPrimaryRangeChange}
              dateRangeId={dateRangeId}
              onDateRangeIdChange={onDateRangeChange}
              comparison={comparisonEnabled ? comparison : null}
              onComparisonChange={setComparison}
              interval={interval}
              onIntervalChange={setInterval}
              primaryRangeOptions={primaryRangeOptionsForInterval}
              intervalOptions={intervals}
              retentionTimestamp={showRetention ? retentionTimestamp : undefined}
              focusComparison={focusComparison}
              onMetricDetailForm={onMetricDetailForm}
            />
            <Pane padding={8} width="100%" borderTop="standard">
              <Flex justifyContent={!validCustomRange ? 'space-between' : 'flex-end'}>
                {!validCustomRange && (
                  <Flex justifyContent="flex-start">
                    <Box marginRight={16}>
                      <DeprecatedIcon name="error-warning-fill" color="yellow-base" size={16} />
                    </Box>
                    <Typography variant="body2">{`Selected time range(s) must be ${maxNumDays} days or less.`}</Typography>
                  </Flex>
                )}
                <Flex justifyContent="flex-end">
                  <Box marginRight={16}>
                    <Button onClick={onCancel} variant="minimal" testId={testIds.BUTTON_ON_CANCEL}>
                      <FormattedMessage
                        defaultMessage="Cancel"
                        id="TblT2P"
                        description="Cancel date/time selection and close the popup"
                      />
                    </Button>
                  </Box>
                  <Button
                    variant="fill-primary"
                    testId={testIds.BUTTON_ON_APPLY}
                    disabled={applyDisabled}
                    onClick={onApplyClick}
                  >
                    <FormattedMessage
                      defaultMessage="Apply"
                      id="HMl7ew"
                      description="Apply the date/time selection and close the popup"
                    />
                  </Button>
                </Flex>
              </Flex>
            </Pane>
          </Flex>
        </Pane>
      </Popover>
    </Flex>
  );
};

interface State {
  primaryRange: DateRange | null;
  dateRangeId: DateRangeID | null;
  comparison: DateRangeComparison | null;
  interval: TimeInterval | null;
}

function toValues(state: State, now: Date): Values {
  let primaryRange = null;
  if (state.dateRangeId) {
    if (state.dateRangeId in dateRangeById) {
      const range = dateRangeById[state.dateRangeId as keyof typeof dateRangeById].range;
      primaryRange = { startTime: range.Start, endTime: range.End };
    } else if (state.dateRangeId === DateRangeID.Custom) {
      if (!state.primaryRange?.start || !state.primaryRange.end) {
        throw new Error('cannot save partial time range');
      }
      primaryRange = {
        startTime: state.primaryRange.start.toISOString(),
        endTime: state.primaryRange.end.toISOString(),
      };
    } else {
      // 'Since' case
      if (!state.primaryRange?.start) {
        throw new Error('must have range start');
      }
      primaryRange = {
        startTime: state.primaryRange.start.toISOString(),
        endTime: sinceDateRangeEnd,
      };
    }
  }

  if (
    state.comparison?.custom &&
    (!state.comparison.custom.start || !state.comparison.custom.end)
  ) {
    throw new Error('cannot save partial custom comparison range');
  }
  const comparison = toTimeRangeComparison(state.comparison, now);

  const values = {
    interval: state.interval,
    primaryRange,
    comparison,
  };
  return values;
}

function getRangeButtonText(
  state: Pick<State, 'primaryRange' | 'dateRangeId' | 'interval'>,
  dateRanges: DateRangeOption[],
  intervals: IntervalOption[],
  placeholder: string | undefined,
): string {
  let intervalText = '';
  if (state.interval) {
    const text = intervals.find(i => i.id === state.interval)?.label;
    if (text) {
      intervalText = `${text} · `;
    }
  }

  let rangeText = '';
  if (state.dateRangeId === DateRangeID.Custom && state.primaryRange) {
    rangeText = `${capitalize(dateRangeToString(state.primaryRange))}`;
  } else if (state.dateRangeId === DateRangeID.Since) {
    if (!state.primaryRange?.start) {
      rangeText = intl.formatMessage({ defaultMessage: 'Custom time range', id: 'uC//jD' });
    } else {
      rangeText = intl.formatMessage(
        { defaultMessage: 'Since {date, date, medium}', id: 'D7qVfW' },
        { date: state.primaryRange.start },
      );
    }
  } else {
    const item = dateRanges.find(x => x.id === state.dateRangeId);
    if (!item) {
      rangeText =
        placeholder ?? intl.formatMessage({ defaultMessage: 'Select a Time Range', id: 'wh47Hb' });
    } else {
      rangeText = item.label;
    }
  }

  return `${intervalText}${rangeText}`;
}

function getCompareButtonText(comparison: DateRangeComparison): string {
  if (comparison.canned !== undefined) {
    return intl.formatMessage(
      { defaultMessage: 'Compared to {dateRange}', id: '0N4Hzp' },
      { dateRange: getCompareText(comparison).toLocaleLowerCase() },
    );
  }
  return intl.formatMessage(
    { defaultMessage: 'Compared to {dateRange}', id: '0N4Hzp' },
    { dateRange: getCompareText(comparison) },
  );
}

interface CompareButtonProps {
  comparison: DateRangeComparison | null;
  onClick: () => void;
  onClear: () => void;
  buttonProps?: ButtonProps;
  disabled: boolean;
  disabledTooltip?: string;
}

const CompareButton: FC<CompareButtonProps> = ({
  comparison,
  onClick,
  onClear,
  buttonProps,
  disabled,
  disabledTooltip,
}) => {
  const compareButton = (
    <Button
      testId={testIds.BUTTON_COMPARE}
      variant="fill-builder"
      {...buttonProps}
      onClick={onClick}
      disabled={disabled}
    >
      <FormattedMessage defaultMessage="Compare to past" id="TzmzCn" />
    </Button>
  );
  if (!comparison) {
    return (
      <>
        {disabledTooltip && disabled ? (
          <Tooltip tooltipContent={disabledTooltip}>{compareButton}</Tooltip>
        ) : (
          compareButton
        )}
      </>
    );
  }

  return (
    // Special case where intent and variant should override caller passed in options
    <InputGroup data-component="CompareButton">
      <Button
        testId={testIds.BUTTON_COMPARE_CLEAR}
        onClick={onClear}
        size={buttonProps?.size}
        variant="outline"
      >
        <DeprecatedIcon name="close" size={16} color="blue-base" />
      </Button>
      <Button
        variant="outline"
        size={buttonProps?.size}
        onClick={onClick}
        testId={testIds.BUTTON_COMPARE}
      >
        {getCompareButtonText(comparison)}
      </Button>
    </InputGroup>
  );
};

function isCustomRangeValid(range: DateRange, maxNumDays?: number): boolean {
  if (!maxNumDays || !range.end) return true;

  const startDate = moment(range.start ?? undefined);
  const endDate = moment(range.end ?? undefined);

  const diff = endDate.diff(startDate, 'days');
  return diff <= maxNumDays;
}
